import React from 'react';
import { Link } from 'react-router-dom';
import { Plus, Palmtree } from 'lucide-react';
import api from '../../utils/axios';
import { useAuth } from '../../contexts/AuthContext';
import useCardData from './useCardData';
import {
  DashboardCard, CardLink, CardLoading, CardError, CardEmpty,
} from './DashboardCard';

const BalanceTile = ({ balance }) => {
  const total = Number(balance.total_quota) || 0;
  const used = Number(balance.used_days) || 0;
  const remaining = Math.max(0, total - used);
  const pct = total > 0 ? Math.min(100, Math.round((used / total) * 100)) : 0;
  // Same thresholds as the My Leave Balances page.
  const barColor = pct >= 90 ? 'bg-danger-500' : pct >= 70 ? 'bg-warning-500' : 'bg-success-500';
  return (
    <li data-testid="dashboard-time-off-tile" className="py-2.5 min-w-0">
      <div className="flex items-baseline justify-between gap-3">
        <span className="text-sm font-medium text-gray-700 truncate" title={balance.leave_type_name}>
          {balance.leave_type_name}
        </span>
        <span className="flex-shrink-0 text-sm text-gray-500 tabular-nums">
          <span className="text-base font-semibold text-gray-900">{remaining}</span>
          {` / ${total} days left`}
        </span>
      </div>
      <div
        className="mt-1.5 h-1.5 rounded-full bg-gray-100 overflow-hidden"
        role="progressbar"
        aria-label={`${balance.leave_type_name} used`}
        aria-valuetext={`${used} of ${total} days used`}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={pct}
      >
        <div className={`h-full rounded-full ${barColor}`} style={{ width: `${pct}%` }} />
      </div>
    </li>
  );
};

/**
 * The signed-in employee's leave balances for this year, one tile per balance-tracked
 * leave type (GET /api/leaves/balances/me, the My Leave Balances page's source).
 */
const TimeOffCard = ({ className = '' }) => {
  const { hasPermission } = useAuth();
  const canApply = hasPermission('leaves', 'create_own');
  const balances = useCardData(
    async () => (await api.get('/api/leaves/balances/me')).data.data
      .filter((balance) => balance.is_balance_tracked !== false),
    []
  );

  return (
    <DashboardCard
      data-testid="dashboard-time-off"
      title="Time off"
      icon={Palmtree}
      className={className}
      action={<CardLink to="/leaves/balances">Balances</CardLink>}
    >
      {balances.loading && <CardLoading rows={2} label="Loading leave balances" />}
      {balances.error && (
        <CardError
          data-testid="dashboard-time-off-error"
          message="Could not load your leave balances."
          onRetry={balances.reload}
        />
      )}
      {balances.data && balances.data.length === 0 && (
        <CardEmpty icon={Palmtree} data-testid="dashboard-time-off-empty">
          No leave balances for this year yet.
        </CardEmpty>
      )}
      {balances.data && balances.data.length > 0 && (
        <ul className="-mt-2.5 divide-y divide-gray-100 max-h-80 overflow-y-auto pr-1">
          {balances.data.map((balance) => <BalanceTile key={balance.id} balance={balance} />)}
        </ul>
      )}
      {canApply && (
        <Link
          to="/leaves/new"
          data-testid="dashboard-time-off-apply"
          className="mt-4 inline-flex items-center justify-center gap-1.5 self-start rounded-lg bg-primary-600 px-3 py-2 text-sm font-medium text-white hover:bg-primary-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2"
        >
          <Plus className="h-4 w-4" aria-hidden="true" />
          Apply for leave
        </Link>
      )}
    </DashboardCard>
  );
};

export default TimeOffCard;
